/**
 * "वोट डाला" (N-PAC, 31 Oct 2026): the polling-day booth agent marks each voter who has voted — serial
 * number typed, the voter slip's QR scanned, or a supporter ticked in the list — and sees the supporters of
 * the booth who have not come yet, house by house, to fetch them.
 *
 * The voter list's oath first (every 12 hours, recorded). A mark goes into a small queue on the phone
 * first and is sent at once; without network it waits and is retried every 15 seconds and when the
 * network returns, so no mark is lost. Names come from the server only (nothing of the voter list is kept
 * on the phone) and are shown for this booth only.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "@core/api";
import { findBoothByCode, normalizeDigits } from "@core/boothCode";
import { apiErrorText } from "@core/messages";
import type { VotedCounts, VoterOath } from "../field/api";
import { addMark, dropSent, parseSerial, queueKey, readQueue, readSlipQr, SEND_BATCH, type MarkVia, type QueuedMark } from "../field/voted";
import { prefs } from "../platform/browser";
import { useApp, useQuery, useSession } from "../ui/context";
import { Banner, Button, Card, Field } from "../ui/components";
import { QrScanner } from "../ui/QrScanner";

type Msg = { kind: "ok" | "warn" | "error" | "info"; text: string } | null;
const RETRY_MS = 15_000;

export function VotedScreen() {
  const { app, t, lang } = useApp();
  const { assemblyId } = useSession();
  const [oath, setOath] = useState<VoterOath | null>(null);
  const [oathState, setOathState] = useState<"checking" | "needed" | "ok" | "offline">("checking");
  const [oathBusy, setOathBusy] = useState(false);
  const [oathErr, setOathErr] = useState<string | null>(null);

  const checkOath = useCallback(() => {
    setOathState("checking");
    app.online((api, aid) => api.voterOath(aid)).then(
      (o) => {
        setOath(o);
        setOathState(o.agreedAt ? "ok" : "needed");
      },
      (e) => {
        if (e instanceof ApiError) {
          setOathErr(apiErrorText(e, lang) ?? e.message);
          setOathState("needed");
        } else setOathState("offline");
      }
    );
  }, [app, lang]);
  useEffect(() => checkOath(), [checkOath]);

  const agree = async () => {
    if (!oath) return;
    setOathBusy(true);
    setOathErr(null);
    try {
      await app.online((api, aid) => api.agreeOath(aid, oath.version));
      setOathState("ok");
    } catch (e) {
      setOathErr(e instanceof ApiError ? apiErrorText(e, lang) ?? e.message : t.netNeeded);
    } finally {
      setOathBusy(false);
    }
  };

  // the login's booths
  const all = useQuery(async () => (assemblyId ? app.core.store.listBooths(assemblyId) : []), [assemblyId], ["booths"]) ?? [];
  const mine = useMemo(() => all.filter((b) => app.canEdit(b)), [all, app]);
  const [code, setCode] = useState("");
  useEffect(() => {
    void app.lastBooth().then((c) => {
      if (c && mine.some((b) => b.code === c)) setCode((cur) => cur || c);
      else if (mine.length && mine.length <= 12) setCode((cur) => cur || mine[0].code);
    });
  }, [app, mine]);
  const booth = useMemo(() => (code.trim() ? findBoothByCode(mine, code) : null), [mine, code]);
  const boothProblem = !code.trim() || booth ? null : findBoothByCode(all, code) ? t.boothNotAssigned(normalizeDigits(code.trim()).toUpperCase()) : t.boothUnknown(normalizeDigits(code.trim()).toUpperCase());

  if (oathState === "checking") return <p className="muted">…</p>;
  if (oathState === "needed")
    return (
      <div className="stack" data-testid="voted-oath">
        <h2 style={{ margin: 0 }}>🗳 {t.vtTitle}</h2>
        <Card>
          <h3 style={{ marginTop: 0 }}>📜 {t.vtOathTitle}</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            {t.vtOathWhy}
          </p>
          {oath ? (
            <ol className="oath">
              {(lang === "hi" ? oath.text.hi : oath.text.en).map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ol>
          ) : null}
          {oathErr ? <Banner kind="error">{oathErr}</Banner> : null}
          {oath ? (
            <Button big busy={oathBusy} onClick={() => void agree()} testId="oath-agree">
              ✋ {t.vtOathAgree}
            </Button>
          ) : (
            <Button variant="secondary" onClick={checkOath}>
              {t.reload}
            </Button>
          )}
        </Card>
      </div>
    );

  return (
    <div data-testid="voted">
      <h2 style={{ margin: "0 0 4px" }}>🗳 {t.vtTitle}</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        {t.vtExplain}
      </p>
      {!mine.length ? <Banner kind="warn">{t.vtNoBooth}</Banner> : null}
      {mine.length > 0 && mine.length <= 12 ? (
        <div className="field">
          <div className="label">{t.vtBoothPick}</div>
          <div className="chips">
            {mine.map((b) => (
              <button
                key={b.code}
                type="button"
                className={`chip${booth?.code === b.code ? " chip-on" : ""}`}
                onClick={() => {
                  setCode(b.code);
                  void app.rememberBooth(b.code);
                }}
                data-testid={`voted-booth-${b.code}`}
              >
                {t.boothNo} {b.code}
              </button>
            ))}
          </div>
          {booth ? <div className="hint">{`${booth.name}${booth.panchayat ? ` • ${booth.panchayat}` : ""}`}</div> : null}
        </div>
      ) : mine.length > 12 ? (
        <Field label={t.vtBoothNo} name="voted-booth" value={code} onChange={setCode} placeholder={t.boothNoHint} error={boothProblem} hint={booth ? `${booth.name} • ${booth.panchayat ?? ""}` : undefined} />
      ) : null}
      {booth && assemblyId ? <BoothDay key={`${assemblyId}:${booth.code}`} aid={assemblyId} code={booth.code} offlineOath={oathState === "offline"} onOath={() => setOathState("needed")} /> : null}
    </div>
  );
}

function BoothDay({ aid, code, offlineOath, onOath }: { aid: string; code: string; offlineOath: boolean; onOath: () => void }) {
  const { app, t, lang } = useApp();
  const [day, setDay] = useState<VotedCounts | null>(null);
  const [loadErr, setLoadErr] = useState<Msg>(offlineOath ? { kind: "info", text: t.vtNeedsNet } : null);
  const [queue, setQueueState] = useState<QueuedMark[]>(() => readQueue(prefs.get(queueKey(aid, code))));
  const queueRef = useRef(queue);
  const setQueue = (q: QueuedMark[]) => {
    queueRef.current = q;
    prefs.set(queueKey(aid, code), q.length ? JSON.stringify(q) : null);
    setQueueState(q);
  };
  const [serial, setSerial] = useState("");
  const [serialErr, setSerialErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<Msg>(null);
  const [last, setLast] = useState<number | null>(null);
  const [scan, setScan] = useState(false);
  const [busy, setBusy] = useState(false);
  const sending = useRef(false);
  // a slower list fetch must not overwrite the counts of a mark sent after it
  const seq = useRef(0);
  const dayRef = useRef(day);
  dayRef.current = day;

  const apiFail = useCallback(
    (e: unknown): Msg => {
      if (e instanceof ApiError) {
        if (e.code === "oath_required") onOath();
        return { kind: "error", text: apiErrorText(e, lang) ?? e.message };
      }
      return null;
    },
    [lang, onOath]
  );

  const load = useCallback(() => {
    const my = ++seq.current;
    app.online((api) => api.voted(aid, code)).then(
      (d) => {
        if (my !== seq.current) return;
        setDay(d);
        setLoadErr(null);
      },
      (e) => setLoadErr(apiFail(e) ?? { kind: "info", text: t.vtNeedsNet })
    );
  }, [app, aid, code, apiFail, t]);

  /** send what is queued; returns the server's answer for the batch, or null (offline / refused) */
  const flush = useCallback(async () => {
    if (sending.current || !queueRef.current.length) return null;
    sending.current = true;
    const batch = queueRef.current.slice(0, SEND_BATCH);
    let sent = false;
    try {
      const r = await app.online((api) => api.markVoted(aid, code, batch.map((m) => ({ serial: m.serial, via: m.via }))));
      setQueue(dropSent(queueRef.current, batch.map((m) => m.serial)));
      seq.current++;
      setDay(r);
      setLoadErr(null);
      sent = true;
      return r;
    } catch (e) {
      const m = apiFail(e);
      if (m) setMsg(m);
      return null;
    } finally {
      sending.current = false;
      // marks added while this batch was on its way go next
      if (sent && queueRef.current.length) setTimeout(() => void flush(), 50);
    }
  }, [app, aid, code, apiFail]);

  useEffect(() => {
    load();
    void flush();
    const again = () => void flush().then((r) => (r ? null : load()));
    const id = window.setInterval(() => void flush(), RETRY_MS);
    window.addEventListener("online", again);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("online", again);
    };
  }, [load, flush]);

  const nameOf = (s: number) => dayRef.current?.pending.find((p) => p.serial === s)?.name ?? dayRef.current?.recent.find((p) => p.serial === s)?.name ?? null;

  const mark = async (s: number, via: MarkVia) => {
    setSerialErr(null);
    setQueue(addMark(queueRef.current, { serial: s, via, at: Date.now() }));
    setLast(s);
    setBusy(true);
    // a batch already on its way: wait for it, then send this one
    for (let i = 0; sending.current && i < 100; i++) await new Promise((ok) => setTimeout(ok, 100));
    const r = await flush();
    setBusy(false);
    if (!r) {
      if (queueRef.current.some((q) => q.serial === s)) {
        const n = nameOf(s);
        setMsg({ kind: "info", text: n ? `${t.vtQueued(s)} — ${n}` : t.vtQueued(s) });
      } else {
        // sent with another batch a moment ago
        const done = dayRef.current?.recent.find((x) => x.serial === s);
        if (done) setMsg({ kind: "ok", text: t.vtMarked(s, done.name, done.supporter) });
      }
      return;
    }
    const ch = r.changed.find((x) => x.serial === s);
    const al = r.already.find((x) => x.serial === s);
    if (ch) setMsg({ kind: "ok", text: t.vtMarked(s, ch.name ?? "—", ch.supporter) });
    else if (al) setMsg({ kind: "warn", text: t.vtAlready(s, al.name) });
    else if (r.unknown.includes(s)) {
      setMsg({ kind: "error", text: t.vtUnknown(s) });
      setLast(null);
    }
  };

  const submitSerial = () => {
    const s = parseSerial(serial);
    if (s == null) return setSerialErr(t.vtSerialBad);
    setSerial("");
    void mark(s, "serial");
  };

  const undo = async () => {
    if (last == null) return;
    const s = last;
    if (queueRef.current.some((q) => q.serial === s)) {
      setQueue(queueRef.current.filter((q) => q.serial !== s));
      setLast(null);
      setMsg({ kind: "info", text: t.vtUndone(s) });
      return;
    }
    setBusy(true);
    try {
      const r = await app.online((api) => api.markVoted(aid, code, [{ serial: s, via: "serial" }], true));
      seq.current++;
      setDay(r);
      setLast(null);
      setMsg({ kind: "info", text: t.vtUndone(s) });
    } catch (e) {
      setMsg(apiFail(e) ?? { kind: "warn", text: t.vtUndoNeedsNet });
    } finally {
      setBusy(false);
    }
  };

  const onScan = (text: string) => {
    const r = readSlipQr(text, aid, code);
    if (r.ok) {
      if (navigator.vibrate) navigator.vibrate(80);
      void mark(r.serial, "qr");
    } else setMsg({ kind: "error", text: r.reason === "other_assembly" ? t.vtOtherAssembly(r.assembly) : r.reason === "other_booth" ? t.vtOtherBooth(r.booth) : t.vtNotSlip });
  };

  const queued = new Set(queue.map((q) => q.serial));
  const pending = (day?.pending ?? []).filter((p) => !queued.has(p.serial));
  const fmt = (n: number) => n.toLocaleString("en-IN");

  return (
    <div className="stack">
      {day ? (
        <div className="totals vt-totals" data-testid="voted-counts">
          <div className="stats">
            <div className="stat">
              <b>
                {fmt(day.voted)}
                <small> / {fmt(day.voters)}</small>
              </b>
              <span>{t.vtVoted}</span>
            </div>
            <div className="stat">
              <b>
                {fmt(day.supportersVoted)}
                <small> / {fmt(day.supporters)}</small>
              </b>
              <span>{t.vtSupporters}</span>
            </div>
            <div className={`stat${day.supporters - day.supportersVoted > 0 ? " stat-warn" : ""}`}>
              <b>{fmt(Math.max(0, day.supporters - day.supportersVoted))}</b>
              <span>{t.vtPendingN}</span>
            </div>
          </div>
        </div>
      ) : null}
      {loadErr ? <Banner kind={loadErr.kind}>{loadErr.text}</Banner> : null}
      {day && !day.voters ? <Banner kind="warn">{t.vtNoVoters}</Banner> : null}
      {queue.length ? (
        <Banner kind="warn" action={{ label: t.vtSendNow, onClick: () => void flush() }} testId="voted-unsent">
          {t.vtUnsent(queue.length)}
        </Banner>
      ) : null}

      <Card>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submitSerial();
          }}
        >
          <Field
            label={t.vtSerial}
            name="voted-serial"
            value={serial}
            onChange={(v) => {
              setSerial(v);
              setSerialErr(null);
            }}
            inputMode="numeric"
            maxLength={6}
            autoComplete="off"
            placeholder={t.vtSerialPh}
            className="vt-serial"
            error={serialErr}
          />
          <Button type="submit" big busy={busy} disabled={!serial.trim()} testId="voted-mark">
            {t.vtMark}
          </Button>
        </form>
        {msg ? (
          <div className={`vt-msg vt-${msg.kind}`} role="status" data-testid="voted-msg">
            {msg.text}
          </div>
        ) : null}
        <div className="vt-actions">
          {scan ? null : (
            <Button variant="secondary" onClick={() => setScan(true)} testId="voted-scan">
              {t.vtScan}
            </Button>
          )}
          {last != null ? (
            <Button variant="ghost" onClick={() => void undo()} disabled={busy} testId="voted-undo">
              {t.vtUndo}
            </Button>
          ) : null}
        </div>
        {scan ? (
          <div style={{ marginTop: 12 }}>
            <QrScanner t={t} onCode={onScan} onClose={() => setScan(false)} />
          </div>
        ) : null}
      </Card>

      <p className="vt-privacy">{t.vtPrivacy}</p>

      {day ? (
        <Card testId="voted-pending">
          <h3 style={{ margin: "0 0 8px" }}>
            {t.vtPendingTitle} <span className="vt-n">{fmt(pending.length)}</span>
          </h3>
          {!day.supporters ? (
            <p className="muted">{t.vtNoSupporters}</p>
          ) : !pending.length ? (
            <p style={{ fontWeight: 800, color: "var(--ok)" }}>{t.vtPendingNone}</p>
          ) : (
            <ul className="vt-list">
              {pending.map((p) => (
                <li key={p.serial}>
                  <span className="vt-house">
                    <small>{t.vtHouse}</small>
                    {p.houseNo || "—"}
                  </span>
                  <span className="vt-who">
                    <b>{p.name}</b>
                    <small>
                      {p.relativeName ? `${p.relativeName} · ` : ""}
                      {p.age} · #{p.serial}
                    </small>
                  </span>
                  <button type="button" className="vt-came" onClick={() => void mark(p.serial, "list")} data-testid={`voted-came-${p.serial}`}>
                    {t.vtCame}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      ) : null}

      {day?.recent.length ? (
        <Card>
          <h3 style={{ margin: "0 0 8px" }}>{t.vtRecent}</h3>
          <ul className="vt-recent">
            {day.recent.map((r) => (
              <li key={`${r.serial}-${r.at}`}>
                <span className="num">#{r.serial}</span> <b>{r.name}</b>
                {r.supporter ? " ★" : ""}
                <small className="muted">
                  {" "}
                  · {new Date(r.at).toLocaleTimeString(lang === "hi" ? "hi-IN" : "en-IN", { hour: "numeric", minute: "2-digit", timeZone: "Asia/Kolkata" })} · {t.vtVia(r.via)}
                </small>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  );
}
